package ai.jessie.web.invoices

import ai.jessie.shared.invoices.InvoiceResponse
import ai.jessie.web.theme.BadgeTone
import ai.jessie.web.theme.CardStyle
import ai.jessie.web.theme.DefinitionsStyle
import ai.jessie.web.theme.HintStyle
import ai.jessie.web.theme.PreLineStyle
import ai.jessie.web.theme.StatBarStyle
import ai.jessie.web.theme.badge
import ai.jessie.web.theme.card
import androidx.compose.runtime.Composable
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.TagElement
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

// Step 11: the stored invoice exactly as returned. Numbering, issue, dispatch and editing belong to
// the separate progression workflow; a Draft has no issued PDF (UC-015 A5). The stat bar carries
// the dates; the state badge sits beside the page title. The recipient and
// address are read-only.
@Composable
internal fun invoiceRecord(current: InvoiceResponse) {
    Div(attrs = CardStyle.toAttrs()) {
        TagElement<HTMLElement>("dl", StatBarStyle.toAttrs()) {
            current.number?.let { number -> recordStat("Rechnungsnummer") { Text(number) } }
            recordStat("Rechnungsdatum") { Text(current.issueDate) }
            recordStat("Fälligkeitsdatum") { Text(current.dueDate) }
            current.sentOn?.let { date -> recordStat("Versanddatum") { Text(date) } }
            current.paidOn?.let { date -> recordStat("Zahlungsdatum") { Text(date) } }
            recordStat("Währung") { Text(current.currency) }
        }
    }
    invoicePositions(current.currency) {
        invoiceLines(current.lines, current.currency)
        invoiceTotals(current.totals, current.currency)
    }
    card(title = "Rechnungsadresse") {
        TagElement<HTMLElement>("dl", DefinitionsStyle.toAttrs()) {
            TagElement<HTMLElement>("dt", null) { Text("Empfänger") }
            TagElement<HTMLElement>("dd", null) { Text(current.recipientName) }
            TagElement<HTMLElement>("dt", null) { Text("Adresse") }
            TagElement<HTMLElement>("dd", PreLineStyle.toAttrs()) {
                Text(addressLines(current.recipientAddress).joinToString("\n"))
            }
        }
    }
    if (current.status == DRAFT) {
        P(attrs = HintStyle.toAttrs()) {
            Text("Die weitere Bearbeitung des Entwurfs folgt in einem separaten Arbeitsschritt.")
        }
        P(attrs = HintStyle.toAttrs()) { Text(notIssuedProblem().message) }
    }
}

@Composable
private fun recordStat(term: String, value: @Composable () -> Unit) {
    Div {
        TagElement<HTMLElement>("dt", null) { Text(term) }
        TagElement<HTMLElement>("dd", null) { value() }
    }
}

/** The state badge: Draft neutral, Ready primary, Sent awaiting payment, Paid done, Cancelled. */
@Composable
internal fun statusBadge(status: String) {
    badge(
        invoiceStatusLabel(status),
        when (status) {
            READY -> BadgeTone.PRIMARY
            SENT -> BadgeTone.WARNING
            PAID -> BadgeTone.HOURS
            CANCELLED -> BadgeTone.DANGER
            else -> BadgeTone.NEUTRAL
        },
    )
}
